import { Link } from 'react-router-dom';
import { Check, Star, ArrowRight, Brain, User, Clock, Skull, Circle, Cpu, Mic, Calendar, Ghost, Lock, Palette, Flame, Moon, Compass } from 'lucide-react';
import { Room } from '@/types/room';
import { useProgression } from '@/hooks/useProgression';
import { useLanguage } from '@/i18n/LanguageContext';
import BookmarkButton from './BookmarkButton';

interface RoomCardProps {
  room: Room;
  variant?: 'default' | 'compact' | 'featured';
  animationDelay?: number;
  className?: string;
  showBookmark?: boolean;
}

const categoryConfig: Record<string, { border: string; accent: string; icon: typeof Brain }> = {
  truth: { border: 'border-l-blue-400/30', accent: 'bg-blue-400/5', icon: Brain },
  identity: { border: 'border-l-purple-400/30', accent: 'bg-purple-400/5', icon: User },
  time: { border: 'border-l-amber-400/30', accent: 'bg-amber-400/5', icon: Clock },
  mortality: { border: 'border-l-rose-400/30', accent: 'bg-rose-400/5', icon: Skull },
  nothing: { border: 'border-l-stone/30', accent: 'bg-muted/30', icon: Circle },
  meta: { border: 'border-l-cyan-400/30', accent: 'bg-cyan-400/5', icon: Cpu },
  voice: { border: 'border-l-emerald-400/30', accent: 'bg-emerald-400/5', icon: Mic },
  daily: { border: 'border-l-blue-300/30', accent: 'bg-blue-300/5', icon: Calendar },
  scary: { border: 'border-l-red-500/30', accent: 'bg-red-500/5', icon: Ghost },
  secret: { border: 'border-l-violet-400/30', accent: 'bg-violet-400/5', icon: Lock },
  dreams: { border: 'border-l-indigo-400/30', accent: 'bg-indigo-400/5', icon: Moon },
  creativity: { border: 'border-l-amber-300/30', accent: 'bg-amber-300/5', icon: Palette },
  fear: { border: 'border-l-red-400/30', accent: 'bg-red-400/5', icon: Flame },
  future: { border: 'border-l-teal-400/30', accent: 'bg-teal-400/5', icon: Compass },
};

const RoomCard = ({ 
  room, 
  variant = 'default', 
  animationDelay = 0,
  className = '',
  showBookmark = true
}: RoomCardProps) => {
  const { isRoomCompleted, state } = useProgression();
  const { t } = useLanguage();
  
  const isCompleted = isRoomCompleted(room.id);
  const isBossRoom = room.id.startsWith('boss-');
  const isNew = !state.completedRooms.includes(room.id) && state.completedRooms.length > 0;
  const config = categoryConfig[room.category] || { border: 'border-l-foreground/10', accent: 'bg-muted/20', icon: Circle };
  const CategoryIcon = config.icon;

  const getTitle = () => {
    const key = `room_${room.id}`;
    const translated = t(key);
    return translated !== key ? translated : room.title;
  };

  const getSubtitle = () => {
    const key = `sub_${room.id}`;
    const translated = t(key);
    return translated !== key ? translated : room.subtitle;
  };

  if (variant === 'compact') {
    return (
      <Link
        to={room.path}
        className={`group flex items-center gap-3 p-3 border border-border/10 rounded-lg bg-card/30 hover:bg-card/60 hover:border-border/20 transition-all duration-300 active:scale-[0.98] ${className}`}
        style={{ animationDelay: `${animationDelay}ms` }}
      >
        {isCompleted && <Check className="w-3 h-3 text-foreground/40" />}
        <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors duration-300">
          {getTitle()}
        </span>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={room.path}
        className={`group relative block overflow-hidden rounded-xl border transition-all duration-500 ${
          isBossRoom 
            ? 'border-foreground/15 bg-gradient-to-br from-foreground/[0.03] via-transparent to-transparent hover:border-foreground/30'
            : 'border-border/10 hover:border-foreground/15 bg-card/30 hover:bg-card/50'
        } hover:-translate-y-1 hover:shadow-[0_8px_40px_-12px_hsl(var(--foreground)/0.08)] ${className}`}
        style={{ animationDelay: `${animationDelay}ms` }}
      >
        {/* Subtle gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/[0.01] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        <div className="relative p-6 md:p-8">
          <div className="flex items-start justify-between mb-4">
            <span className="text-[9px] text-muted-foreground/25 uppercase tracking-[0.2em] font-medium">
              {room.category}
            </span>
            <div className="flex items-center gap-2">
              {isNew && !isCompleted && (
                <div className="w-1 h-1 rounded-full bg-foreground/40 animate-pulse-slow" />
              )}
              {isCompleted && (
                <div className="w-1.5 h-1.5 rounded-full bg-foreground/30" />
              )}
            </div>
          </div>
          <h3 className="text-base md:text-lg font-light text-foreground/80 group-hover:text-foreground transition-colors duration-500 mb-2 tracking-tight">
            {getTitle()}
          </h3>
          <p className="text-muted-foreground/35 text-sm leading-relaxed">
            {getSubtitle()}
          </p>
          
          <div className="absolute bottom-5 right-5 opacity-0 group-hover:opacity-60 transition-all duration-500 transform translate-x-2 group-hover:translate-x-0">
            <ArrowRight className="w-3.5 h-3.5 text-foreground/40" />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={room.path}
      className={`group relative overflow-hidden rounded-xl border border-border/8 border-l-2 ${config.border} ${config.accent} hover:bg-card/60 transition-all duration-500 active:scale-[0.97] hover:-translate-y-1 hover:shadow-[0_8px_40px_-12px_hsl(var(--foreground)/0.06)] ${className}`}
      style={{ 
        animationDelay: `${animationDelay}ms`,
        animation: 'revealUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards',
        opacity: 0 
      }}
    >
      {/* Hover gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-foreground/[0.01] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      <div className="relative p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <CategoryIcon className="w-3 h-3 text-muted-foreground/20 group-hover:text-muted-foreground/40 transition-colors duration-500" />
            <span className="text-[9px] text-muted-foreground/20 uppercase tracking-[0.2em]">
              {room.category}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {showBookmark && (
              <BookmarkButton roomId={room.id} size="sm" />
            )}
            {isNew && !isCompleted && (
              <div className="w-1 h-1 rounded-full bg-foreground/30 animate-pulse-slow" />
            )}
            {isCompleted && (
              <Check className="w-3 h-3 text-foreground/25" />
            )}
          </div>
        </div>
        <h3 className="font-light text-foreground/75 group-hover:text-foreground transition-colors duration-500 text-sm leading-snug tracking-tight">
          {getTitle()}
        </h3>
        <p className="text-muted-foreground/30 text-xs mt-2 line-clamp-2 leading-relaxed group-hover:text-muted-foreground/50 transition-colors duration-500">
          {getSubtitle()}
        </p>
      </div>
    </Link>
  );
};

export default RoomCard;
